import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { Database, Edit2, Save, X, AlertCircle, CheckCircle, Search, RefreshCw } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { Navigate } from 'react-router-dom';
import apiConfig from '../../config/api.config';

// Styled Components
const BaseContainer = styled.div`
  padding: ${({ theme }) => theme.spacing.xl};
  max-width: 100%;
  margin: 0 auto;
`;

const Header = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.xl};
`;

const Title = styled.h1`
  font-size: ${({ theme }) => theme.typography.fontSize['2xl']};
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  color: ${({ theme }) => theme.colors.textPrimary};
  margin: 0 0 ${({ theme }) => theme.spacing.xs} 0;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
`;

const Subtitle = styled.p`
  color: ${({ theme }) => theme.colors.textSecondary};
  margin: 0;
`;

const ControlsBar = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.md};
  margin-bottom: ${({ theme }) => theme.spacing.lg};
  flex-wrap: wrap;
  align-items: center;
`;

const FiltersContainer = styled.div`
  flex: 1;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: ${({ theme }) => theme.spacing.md};
  
  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    grid-template-columns: 1fr 1fr;
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    grid-template-columns: 1fr;
  }
`;

const FilterSelect = styled.select`
  width: 100%;
  padding: ${({ theme }) => theme.spacing.md};
  border: 2px solid ${({ theme }) => theme.colors.gray200};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  background: ${({ theme }) => theme.colors.white};
  color: ${({ theme }) => theme.colors.textPrimary};
  cursor: pointer;
  
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    background: ${({ theme }) => theme.colors.gray100};
  }
`;

const SearchInput = styled.input`
  flex: 1;
  min-width: 250px;
  padding: ${({ theme }) => theme.spacing.md};
  border: 2px solid ${({ theme }) => theme.colors.gray200};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  font-size: ${({ theme }) => theme.typography.fontSize.base};
  
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' | 'success' }>`
  padding: ${({ theme }) => theme.spacing.md} ${({ theme }) => theme.spacing.lg};
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs};
  transition: all 0.2s ease;
  
  ${({ variant, theme }) => {
    if (variant === 'success') {
      return `
        background: #10b981;
        color: white;
        &:hover { background: #059669; }
      `;
    }
    if (variant === 'secondary') {
      return `
        background: ${theme.colors.gray200};
        color: ${theme.colors.textPrimary};
        &:hover { background: ${theme.colors.gray300}; }
      `;
    }
    return `
      background: ${theme.colors.primary};
      color: white;
      &:hover { background: ${theme.colors.primaryDark}; }
    `;
  }}

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const TableContainer = styled.div`
  background: ${({ theme }) => theme.colors.white};
  border-radius: ${({ theme }) => theme.borderRadius.xl};
  box-shadow: ${({ theme }) => theme.shadows.sm};
  overflow: hidden;
`;

const TableWrapper = styled.div`
  overflow-x: auto;
  max-height: 70vh;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  min-width: 1800px;
  
  th, td {
    padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
    text-align: left;
    border-bottom: 1px solid ${({ theme }) => theme.colors.gray200};
    font-size: ${({ theme }) => theme.typography.fontSize.sm};
    white-space: nowrap;
  }
  
  th {
    background: ${({ theme }) => theme.colors.gray50};
    font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
    color: ${({ theme }) => theme.colors.textPrimary};
    position: sticky;
    top: 0;
    z-index: 10;
  }
  
  tbody tr:hover {
    background: ${({ theme }) => theme.colors.gray50};
  }
  
  /* Columnas con ancho específico */
  td:nth-child(1), td:nth-child(2) {
    min-width: 150px;
  }
  
  td:nth-child(3), td:nth-child(4) {
    min-width: 120px;
  }
  
  td:nth-child(12) {
    max-width: 200px;
    white-space: normal;
  }
`;

const Input = styled.input`
  width: 100%;
  padding: ${({ theme }) => theme.spacing.sm};
  border: 2px solid ${({ theme }) => theme.colors.gray200};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: ${({ theme }) => theme.spacing.sm};
  border: 2px solid ${({ theme }) => theme.colors.gray200};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  resize: vertical;
  min-height: 60px;
  
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const ActionButtons = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.xs};
`;

const IconButton = styled.button<{ variant?: 'edit' | 'save' | 'cancel' }>`
  padding: ${({ theme }) => theme.spacing.sm};
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  cursor: pointer;
  display: flex;
  align-items: center;
  transition: all 0.2s ease;
  
  ${({ variant, theme }) => {
    if (variant === 'save') {
      return `
        background: #10b981;
        color: white;
        &:hover { background: #059669; }
      `;
    }
    if (variant === 'cancel') {
      return `
        background: ${theme.colors.gray200};
        color: ${theme.colors.textPrimary};
        &:hover { background: ${theme.colors.gray300}; }
      `;
    }
    return `
      background: ${theme.colors.primary};
      color: white;
      &:hover { background: ${theme.colors.primaryDark}; }
    `;
  }}
`;

const Message = styled.div<{ type: 'error' | 'success' | 'info' }>`
  padding: ${({ theme }) => theme.spacing.md};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  margin-bottom: ${({ theme }) => theme.spacing.md};
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs};
  
  ${({ type }) => {
    if (type === 'error') {
      return `
        background: #fef2f2;
        color: #dc2626;
        border: 1px solid #fecaca;
      `;
    }
    if (type === 'success') {
      return `
        background: #f0fdf4;
        color: #16a34a;
        border: 1px solid #bbf7d0;
      `;
    }
    return `
      background: #eff6ff;
      color: #2563eb;
      border: 1px solid #bfdbfe;
    `;
  }}
`;

const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: ${({ theme }) => theme.spacing.xl};
  color: ${({ theme }) => theme.colors.textSecondary};
`;

// Interfaces
interface RegistroBase {
  origen_ciudad: string;
  origen_departamento: string | null;
  destino_ciudad: string;
  destino_departamento: string | null;
  tipo_vehiculo: string;
  tipo_carga: string;
  configuracion: string;
  valor_sicetac: number;
  viajes_mes: number | null;
  ton_mes: number | null;
  comentarios: string | null;
  disponibilidad: string | null;
  valor_de_tarifa: number | null;
  categoria: string | null;
}

const BaseModule: React.FC = () => {
  const { proveedor } = useAuth();
  const [registros, setRegistros] = useState<RegistroBase[]>([]);
  const [registrosFiltrados, setRegistrosFiltrados] = useState<RegistroBase[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [editingRow, setEditingRow] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<RegistroBase | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Estados para los filtros
  const [filtros, setFiltros] = useState({
    origen_ciudad: '',
    destino_ciudad: '',
    tipo_vehiculo: '',
    tipo_carga: '',
    configuracion: '',
    categoria: '',
    disponibilidad: ''
  });
  
  // Estados para las opciones de los filtros (se extraen de los datos)
  const [opcionesFiltros, setOpcionesFiltros] = useState({
    origenes: [] as string[],
    destinos: [] as string[],
    tipos_vehiculo: [] as string[],
    tipos_carga: [] as string[],
    configuraciones: [] as string[],
    categorias: [] as string[],
    disponibilidades: [] as string[]
  });

  // Función para cargar datos de la base
  const cargarDatos = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetch(apiConfig.endpoints.sicetacTarifas, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });

      if (!response.ok) {
        throw new Error('Error al cargar los datos');
      }

      const data = await response.json();
      const registros = data.registros || [];
      setRegistros(registros);
      setRegistrosFiltrados(registros);
      
      // Extraer opciones únicas para los filtros
      extraerOpcionesFiltros(registros);
    } catch (err: any) {
      setError(err.message || 'Error al cargar los datos');
    } finally {
      setLoading(false);
    }
  }, []);

  // Cargar datos de la base al montar el componente
  useEffect(() => {
    cargarDatos();
  }, [cargarDatos]);

  // Filtrar registros cuando cambian los filtros o el término de búsqueda
  useEffect(() => {
    let filtrados = [...registros];
    
    // Aplicar filtros de dropdown
    if (filtros.origen_ciudad) {
      filtrados = filtrados.filter(reg => reg.origen_ciudad === filtros.origen_ciudad);
    }
    if (filtros.destino_ciudad) {
      filtrados = filtrados.filter(reg => reg.destino_ciudad === filtros.destino_ciudad);
    }
    if (filtros.tipo_vehiculo) {
      filtrados = filtrados.filter(reg => reg.tipo_vehiculo === filtros.tipo_vehiculo);
    }
    if (filtros.tipo_carga) {
      filtrados = filtrados.filter(reg => reg.tipo_carga === filtros.tipo_carga);
    }
    if (filtros.configuracion) {
      filtrados = filtrados.filter(reg => reg.configuracion === filtros.configuracion);
    }
    if (filtros.categoria) {
      filtrados = filtrados.filter(reg => reg.categoria === filtros.categoria);
    }
    if (filtros.disponibilidad) {
      filtrados = filtrados.filter(reg => reg.disponibilidad === filtros.disponibilidad);
    }
    
    // Aplicar búsqueda de texto (opcional, sobre los resultados filtrados)
    if (searchTerm.trim() !== '') {
      const term = searchTerm.toLowerCase();
      filtrados = filtrados.filter(reg => 
        reg.origen_ciudad.toLowerCase().includes(term) ||
        (reg.origen_departamento && reg.origen_departamento.toLowerCase().includes(term)) ||
        reg.destino_ciudad.toLowerCase().includes(term) ||
        (reg.destino_departamento && reg.destino_departamento.toLowerCase().includes(term)) ||
        reg.tipo_vehiculo.toLowerCase().includes(term) ||
        reg.tipo_carga.toLowerCase().includes(term) ||
        reg.configuracion.toLowerCase().includes(term) ||
        (reg.categoria && reg.categoria.toLowerCase().includes(term)) ||
        (reg.comentarios && reg.comentarios.toLowerCase().includes(term))
      );
    }
    
    setRegistrosFiltrados(filtrados);
  }, [searchTerm, registros, filtros]);

  // Verificar que solo el ADMINISTRADOR pueda acceder
  // IMPORTANTE: Esta verificación debe ir DESPUÉS de todos los hooks
  if (proveedor?.username !== 'ADMINISTRADOR') {
    return <Navigate to="/dashboard" replace />;
  }

  // Extraer opciones únicas de los registros para los filtros
  const extraerOpcionesFiltros = (registros: RegistroBase[]) => {
    const origenes = Array.from(new Set(registros.map(r => r.origen_ciudad))).sort();
    const destinos = Array.from(new Set(registros.map(r => r.destino_ciudad))).sort();
    const tipos_vehiculo = Array.from(new Set(registros.map(r => r.tipo_vehiculo))).sort();
    const tipos_carga = Array.from(new Set(registros.map(r => r.tipo_carga))).sort();
    const configuraciones = Array.from(new Set(registros.map(r => r.configuracion))).sort();
    const categorias = Array.from(new Set(registros.map(r => r.categoria).filter(Boolean) as string[])).sort();
    const disponibilidades = Array.from(new Set(registros.map(r => r.disponibilidad).filter(Boolean) as string[])).sort();
    
    setOpcionesFiltros({
      origenes,
      destinos,
      tipos_vehiculo,
      tipos_carga,
      configuraciones,
      categorias,
      disponibilidades
    });
  };

  const handleFilterChange = (campo: keyof typeof filtros, valor: string) => {
    setFiltros(prev => ({
      ...prev,
      [campo]: valor
    }));
  };

  const limpiarFiltros = () => {
    setFiltros({
      origen_ciudad: '',
      destino_ciudad: '',
      tipo_vehiculo: '',
      tipo_carga: '',
      configuracion: '',
      categoria: '',
      disponibilidad: ''
    });
    setSearchTerm('');
  };

  const handleEdit = (registro: RegistroBase) => {
    const key = `${registro.origen_ciudad}-${registro.destino_ciudad}-${registro.tipo_vehiculo}-${registro.tipo_carga}-${registro.configuracion}`;
    setEditingRow(key);
    setEditForm({ ...registro });
  };

  const handleCancel = () => {
    setEditingRow(null);
    setEditForm(null);
  };

  const handleSave = async () => {
    if (!editForm) return;

    try {
      setError(null);
      setSuccess(null);

      const response = await fetch(apiConfig.endpoints.sicetacActualizar, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: JSON.stringify(editForm)
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Error al actualizar el registro');
      }

      setSuccess('Registro actualizado exitosamente');
      setEditingRow(null);
      setEditForm(null);
      
      // Recargar datos
      await cargarDatos();

      // Limpiar mensaje de éxito después de 3 segundos
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.message || 'Error al guardar los cambios');
    }
  };

  const handleInputChange = (field: keyof RegistroBase, value: any) => {
    if (!editForm) return;
    
    setEditForm({
      ...editForm,
      [field]: value
    });
  };

  const getRowKey = (registro: RegistroBase) => {
    return `${registro.origen_ciudad}-${registro.destino_ciudad}-${registro.tipo_vehiculo}-${registro.tipo_carga}-${registro.configuracion}`;
  };

  if (loading) {
    return (
      <BaseContainer>
        <LoadingSpinner>
          <RefreshCw className="animate-spin" size={24} />
          <span style={{ marginLeft: '8px' }}>Cargando datos...</span>
        </LoadingSpinner>
      </BaseContainer>
    );
  }

  return (
    <BaseContainer>
      <Header>
        <Title>
          <Database size={32} />
          Base de Datos - Tarifas
        </Title>
        <Subtitle>
          Vista y edición de la tabla principal de tarifas ({registros.length} registros)
        </Subtitle>
      </Header>

      {error && (
        <Message type="error">
          <AlertCircle size={16} />
          {error}
        </Message>
      )}

      {success && (
        <Message type="success">
          <CheckCircle size={16} />
          {success}
        </Message>
      )}

      <ControlsBar>
        <FiltersContainer>
          <FilterSelect
            value={filtros.origen_ciudad}
            onChange={(e) => handleFilterChange('origen_ciudad', e.target.value)}
          >
            <option value="">📍 Origen (Todas)</option>
            {opcionesFiltros.origenes.map(origen => (
              <option key={origen} value={origen}>{origen}</option>
            ))}
          </FilterSelect>

          <FilterSelect
            value={filtros.destino_ciudad}
            onChange={(e) => handleFilterChange('destino_ciudad', e.target.value)}
          >
            <option value="">🎯 Destino (Todas)</option>
            {opcionesFiltros.destinos.map(destino => (
              <option key={destino} value={destino}>{destino}</option>
            ))}
          </FilterSelect>

          <FilterSelect
            value={filtros.tipo_vehiculo}
            onChange={(e) => handleFilterChange('tipo_vehiculo', e.target.value)}
          >
            <option value="">🚛 Tipo Vehículo (Todos)</option>
            {opcionesFiltros.tipos_vehiculo.map(tipo => (
              <option key={tipo} value={tipo}>{tipo}</option>
            ))}
          </FilterSelect>

          <FilterSelect
            value={filtros.tipo_carga}
            onChange={(e) => handleFilterChange('tipo_carga', e.target.value)}
          >
            <option value="">📦 Tipo Carga (Todos)</option>
            {opcionesFiltros.tipos_carga.map(tipo => (
              <option key={tipo} value={tipo}>{tipo}</option>
            ))}
          </FilterSelect>

          <FilterSelect
            value={filtros.configuracion}
            onChange={(e) => handleFilterChange('configuracion', e.target.value)}
          >
            <option value="">⚙️ Configuración (Todas)</option>
            {opcionesFiltros.configuraciones.map(config => (
              <option key={config} value={config}>{config}</option>
            ))}
          </FilterSelect>

          <FilterSelect
            value={filtros.categoria}
            onChange={(e) => handleFilterChange('categoria', e.target.value)}
          >
            <option value="">🏷️ Categoría (Todas)</option>
            {opcionesFiltros.categorias.map(cat => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </FilterSelect>

          <FilterSelect
            value={filtros.disponibilidad}
            onChange={(e) => handleFilterChange('disponibilidad', e.target.value)}
          >
            <option value="">✓ Disponibilidad (Todas)</option>
            {opcionesFiltros.disponibilidades.map(disp => (
              <option key={disp} value={disp}>{disp}</option>
            ))}
          </FilterSelect>
        </FiltersContainer>

        <Button variant="secondary" onClick={limpiarFiltros}>
          <X size={16} />
          Limpiar
        </Button>

        <Button onClick={cargarDatos} disabled={loading}>
          <RefreshCw size={16} />
          Recargar
        </Button>
      </ControlsBar>

      <div style={{ 
        marginBottom: '1rem', 
        fontSize: '0.9rem', 
        color: '#666',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <span>
          {registrosFiltrados.length === registros.length 
            ? `Mostrando ${registros.length} registros` 
            : `Mostrando ${registrosFiltrados.length} de ${registros.length} registros`}
        </span>
        <SearchInput
          type="text"
          placeholder="🔍 Búsqueda adicional..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{ flex: '0 0 300px', padding: '8px 12px', fontSize: '0.875rem' }}
        />
      </div>

      <TableContainer>
        <TableWrapper>
          <Table>
            <thead>
              <tr>
                <th>Origen</th>
                <th>Destino</th>
                <th>Tipo Vehículo</th>
                <th>Tipo Carga</th>
                <th>Config.</th>
                <th>Valor SICETAC</th>
                <th>Viajes/Mes</th>
                <th>Ton/Mes</th>
                <th>Disponibilidad</th>
                <th>Valor Tarifa</th>
                <th>Categoría</th>
                <th>Comentarios</th>
                <th style={{ width: '100px' }}>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {registrosFiltrados.map((registro) => {
                const key = getRowKey(registro);
                const isEditing = editingRow === key;

                return (
                  <tr key={key}>
                    <td>
                      {registro.origen_ciudad}
                      {registro.origen_departamento && <div style={{fontSize: '0.85em', color: '#666'}}>({registro.origen_departamento})</div>}
                    </td>
                    <td>
                      {registro.destino_ciudad}
                      {registro.destino_departamento && <div style={{fontSize: '0.85em', color: '#666'}}>({registro.destino_departamento})</div>}
                    </td>
                    <td>{registro.tipo_vehiculo}</td>
                    <td>{registro.tipo_carga}</td>
                    <td>{registro.configuracion}</td>
                    <td>
                      {isEditing ? (
                        <Input
                          type="number"
                          value={editForm?.valor_sicetac || 0}
                          onChange={(e) => handleInputChange('valor_sicetac', parseInt(e.target.value))}
                        />
                      ) : (
                        `$${registro.valor_sicetac?.toLocaleString() || 0}`
                      )}
                    </td>
                    <td>
                      {isEditing ? (
                        <Input
                          type="number"
                          value={editForm?.viajes_mes || ''}
                          onChange={(e) => handleInputChange('viajes_mes', e.target.value ? parseInt(e.target.value) : null)}
                        />
                      ) : (
                        registro.viajes_mes || '-'
                      )}
                    </td>
                    <td>
                      {isEditing ? (
                        <Input
                          type="number"
                          step="0.01"
                          value={editForm?.ton_mes || ''}
                          onChange={(e) => handleInputChange('ton_mes', e.target.value ? parseFloat(e.target.value) : null)}
                        />
                      ) : (
                        registro.ton_mes || '-'
                      )}
                    </td>
                    <td>
                      {isEditing ? (
                        <Input
                          type="text"
                          value={editForm?.disponibilidad || ''}
                          onChange={(e) => handleInputChange('disponibilidad', e.target.value)}
                        />
                      ) : (
                        registro.disponibilidad || '-'
                      )}
                    </td>
                    <td>
                      {isEditing ? (
                        <Input
                          type="number"
                          step="0.01"
                          value={editForm?.valor_de_tarifa || ''}
                          onChange={(e) => handleInputChange('valor_de_tarifa', e.target.value ? parseFloat(e.target.value) : null)}
                        />
                      ) : (
                        registro.valor_de_tarifa ? `$${registro.valor_de_tarifa.toLocaleString()}` : '-'
                      )}
                    </td>
                    <td>
                      {isEditing ? (
                        <Input
                          type="text"
                          value={editForm?.categoria || ''}
                          onChange={(e) => handleInputChange('categoria', e.target.value)}
                        />
                      ) : (
                        registro.categoria || '-'
                      )}
                    </td>
                    <td>
                      {isEditing ? (
                        <TextArea
                          value={editForm?.comentarios || ''}
                          onChange={(e) => handleInputChange('comentarios', e.target.value)}
                          placeholder="Comentarios..."
                        />
                      ) : (
                        registro.comentarios || '-'
                      )}
                    </td>
                    <td>
                      {isEditing ? (
                        <ActionButtons>
                          <IconButton variant="save" onClick={handleSave} title="Guardar">
                            <Save size={16} />
                          </IconButton>
                          <IconButton variant="cancel" onClick={handleCancel} title="Cancelar">
                            <X size={16} />
                          </IconButton>
                        </ActionButtons>
                      ) : (
                        <IconButton variant="edit" onClick={() => handleEdit(registro)} title="Editar">
                          <Edit2 size={16} />
                        </IconButton>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </TableWrapper>
      </TableContainer>

      {registrosFiltrados.length === 0 && !loading && (
        <Message type="info">
          <AlertCircle size={16} />
          No se encontraron registros que coincidan con la búsqueda.
        </Message>
      )}
    </BaseContainer>
  );
};

export default BaseModule;

